import { useState } from "react";
import { apiRequest } from "../utils/api";
import { useLanguage } from "../context/LanguageContext";
import { fillText } from "../utils/leadDisplay";

const MAX_CHANGES = 2;

const ANSWERS = [
  { value: "WON", key: "ld_yes" },
  { value: "LOST", key: "hs_no" },
  { value: "PENDING", key: "hs_notYet" },
];

/**
 * "Did you book it?" — the one question the funnel never asked.
 *
 * A customer taps Call and leaves. The trip is arranged on the phone, so there is no
 * booking for us to read: enquiries, calls, and no record of whether a single one
 * became a journey. Without it nothing downstream is knowable — which cars convert,
 * what a listing is worth to a partner, whether a lead fee is fair.
 *
 * Deliberately small and deliberately skippable. It appears once, for the most recent
 * uncalled-for answer, above the list rather than over it, and "Not yet" is a real
 * answer that closes it without lying. A modal demanding an answer before the app can
 * be used would collect better data from fewer people and cost us the visit.
 *
 * Only ever asks about a lead this customer actually called, once the journey date
 * has been and gone, and within a fortnight of it — before the pickup there is nothing
 * to report, and beyond a fortnight they are guessing. The page hands it that lead
 * (the first of /leads/my-recent's `pending`), from the one request it already makes
 * for the customer's enquiries.
 */
export default function LeadOutcomePrompt({ lead }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // The answer as given here, so the card can confirm it and offer a correction
  // without a round trip to re-read what we just wrote.
  const [answer, setAnswer] = useState(null);
  const [changesLeft, setChangesLeft] = useState(MAX_CHANGES);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");

  const send = async (outcome) => {
    if (!lead || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await apiRequest(`/leads/${encodeURIComponent(lead.leadId)}/outcome`, {
        method: "PUT",
        body: JSON.stringify({ outcome }),
      });
      setAnswer(outcome);
      setChangesLeft(Number.isFinite(Number(res?.changesLeft)) ? Number(res.changesLeft) : changesLeft);
      setEditing(false);
    } catch {
      // A refusal is worth showing — it means the change budget is spent, and
      // silently doing nothing would read as a broken button.
      setError(t("hs_saveErr"));
    } finally {
      setBusy(false);
    }
  };

  if (!lead || dismissed) return null;

  // The question names what was enquired about: the car and the partner, or the
  // driver for a hire enquiry. The name as the cards write it - "Satish", not the
  // "SATISH" it was typed as at sign-up.
  const first = String(lead.partnerName || "").trim().split(/\s+/)[0] || "";
  const who = first ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase() : "";
  const question = lead.sourceType === "HIRE_DRIVER"
    ? (who ? fillText(t("hm_didHire"), { name: who }) : t("ld_didBookDriver"))
    : lead.carLabel
      ? fillText(t(who ? "hm_didBookWith" : "hm_didBookCar"), { car: lead.carLabel, name: who })
      : t("ld_didBook");
  const answered = Boolean(answer);
  const chosen = ANSWERS.find((a) => a.value === answer);

  return (
    <section className="rounded-2xl border border-[#D6E6B0] bg-[#F1F7E3] p-3 fade-up">
      {answered && !editing ? (
        <>
          <p className="text-[11.5px] font-bold text-[#1F2937] leading-snug">
            {fillText(t("hm_thanksSaid"), { answer: chosen ? t(chosen.key) : "" })}
          </p>
          <div className="flex items-center gap-2 mt-2">
            {changesLeft > 0 ? (
              <button type="button" onClick={() => setEditing(true)}
                className="min-h-[40px] px-3 rounded-[11px] border border-[#B9D77A] bg-white text-[10.5px] font-bold text-[#3F5E00]">
                {t("hs_changeAnswer")}
              </button>
            ) : (
              <span className="text-[9.5px] text-[#64748B] font-semibold">{t("hs_noMoreChanges")}</span>
            )}
            <button type="button" onClick={() => setDismissed(true)}
              className="ml-auto min-h-[40px] px-3 text-[10.5px] font-bold text-[#475569]">
              {t("hs_close")}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-[11.5px] font-bold text-[#1F2937] leading-snug">
            {editing ? t("hs_changeYour") : question}
          </p>
          <p className="text-[9.5px] text-[#475569] mt-1 leading-snug">
            {editing ? fillText(t("hs_changesLeft"), { n: changesLeft }) : t("hs_askWhy")}
          </p>

          {/* Three equal buttons, none of them filled in.
              "Yes, booked" used to be solid green while the other two were outlined,
              which reads as an answer already chosen — and an answer already chosen is
              the one that gets tapped. Nothing here is selected until the customer
              selects it. */}
          <div className="grid grid-cols-3 gap-2 mt-2.5">
            {ANSWERS.map((a) => (
              <button
                key={a.value}
                type="button"
                disabled={busy}
                onClick={() => send(a.value)}
                className={`min-h-[44px] px-1.5 rounded-xl bg-white border text-[10.5px] font-bold leading-tight disabled:opacity-50 active:scale-[0.98] transition-all ${
                  answer === a.value ? "border-[#7BAE00] text-[#3F5E00]" : "border-[#E2E8F0] text-[#334155]"
                }`}
              >
                {t(a.key)}
              </button>
            ))}
          </div>

          {error && <p className="text-[9.5px] font-semibold text-[#DC2626] mt-2">{error}</p>}

          {!editing && (
            <button type="button" onClick={() => setDismissed(true)}
              className="mt-1.5 min-h-[36px] text-[10.5px] font-semibold text-[#475569]">
              {t("hm_askLater")}
            </button>
          )}
        </>
      )}
    </section>
  );
}
